import type { Vec2 } from '../geo/types';

const ON_RIM_M = 1e-6;

// A point on the far rim, with the arc position on the bounds of the point carried out to it.
export interface RimPoint {
  readonly p: Vec2;
  readonly s: number;
}

interface RayCrossing {
  readonly t: number;
  readonly u: number;
}

// PLAN §5 step 5: the rim is the bounds carried radially out by the far radius.
export function extrude(p: Vec2, center: Vec2, farRadius: number): Vec2 {
  const dx = p[0] - center[0];
  const dy = p[1] - center[1];
  const dist = Math.sqrt(dx * dx + dy * dy);
  const scale = (dist + farRadius) / dist;
  return [center[0] + dx * scale, center[1] + dy * scale];
}

// Where the ray from `from` along the unit `direction` crosses the segment ab: t along the ray and
// u along the segment, or null when it passes by.
export function rayCrossing(from: Vec2, direction: Vec2, a: Vec2, b: Vec2): RayCrossing | null {
  const e: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const denominator = direction[0] * e[1] - direction[1] * e[0];
  if (denominator === 0) return null;
  const w: Vec2 = [a[0] - from[0], a[1] - from[1]];
  const t = (w[0] * e[1] - w[1] * e[0]) / denominator;
  const u = (w[0] * direction[1] - w[1] * direction[0]) / denominator;
  return t > 0 && u >= 0 && u <= 1 ? { t, u } : null;
}

// The rim point where a ray from inside the rim first reaches it; its arc position is interpolated
// along the rim chord it meets, so the rim points of every run stay in one order.
export function rayToRim(rim: readonly RimPoint[], from: Vec2, direction: Vec2): RimPoint {
  let hit: RimPoint | null = null;
  let nearest = Infinity;
  for (let i = 0; i + 1 < rim.length; i++) {
    const [a, b] = [rim[i]!, rim[i + 1]!];
    const crossing = rayCrossing(from, direction, a.p, b.p);
    if (crossing && crossing.t < nearest) {
      nearest = crossing.t;
      hit = {
        p: [from[0] + direction[0] * crossing.t, from[1] + direction[1] * crossing.t],
        s: a.s + (b.s - a.s) * crossing.u,
      };
    }
  }
  if (!hit) throw new Error(`Far ring: the ray from ${from.join(', ')} never meets the rim`);
  return hit;
}

// The rim points strictly between two arc positions, from the later back to the earlier, the way a
// run's outline returns along the rim.
export function rimBetween(rim: readonly RimPoint[], from: number, to: number): readonly Vec2[] {
  return rim
    .filter((point) => point.s > from + ON_RIM_M && point.s < to - ON_RIM_M)
    .map((point) => point.p)
    .reverse();
}
